/*
 * ChicagoTourPage — /chicago-tour
 * 12-р сарын Чикаго хотын Christmas аялал — $3,990
 */
import { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plane,
  Hotel,
  Car,
  Shield,
  UtensilsCrossed,
  Users,
  MapPin,
  CheckCircle2,
  Phone,
  MessageCircle,
  ChevronRight,
  Info,
} from "lucide-react";

/* ── Image URLs ─────────────────────────────────────────────── */
const IMAGES = {
  hero: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/eHSoNGKbEtytarCO.jpg",
  bean: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/GmyxfKDPUVurXATG.jpg",
  riverwalk: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/eftMxigMEezYwbTH.jpg",
  landmarks: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/NkKUQCmDFPaBvlRx.jpg",
  artInstitute: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/WJcKEmgmOnMJLWCr.jpg",
  navyPier: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/MGOTaVZNpiGmMMdA.jpg",
  wrigley: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/tkgiATFOWCJajTSs.jpg",
  hondaOdyssey: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/tSjBNaeGlFPkcWNl.jpg",
  hondaOdyssey2: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/IYZFUCiKrgDhAkQI.jpg",
};

/* ── Included services ──────────────────────────────────────── */
const INCLUDED = [
  { icon: Plane, label: "Улаанбаатар–Чикаго–Улаанбаатар хоёр талын онгоцны тийз" },
  { icon: Hotel, label: "Буудал (7 хоног)" },
  { icon: Car, label: "Хот доторх тээвэр — Honda Odyssey minivan (8 суудалтай)" },
  { icon: MapPin, label: "Аяллын хөтөлбөрт багтсан үзвэр үйлчилгээ" },
  { icon: Shield, label: "Аяллын даатгал" },
  { icon: UtensilsCrossed, label: "Өдөр бүрийн 3 удаагийн хоол" },
  { icon: CheckCircle2, label: "АНУ-ын визний улсын хураамж" },
  { icon: Users, label: "Аяллын хөтөч" },
  { icon: CheckCircle2, label: "Бүртгэлийн хураамж" },
];

/* ── Itinerary ──────────────────────────────────────────────── */
const ITINERARY = [
  {
    day: 1,
    title: "Цастай Чикагод ирэх",
    image: IMAGES.hondaOdyssey,
    activities: [
      "O'Hare нисэх буудал дээр тосох",
      "Буудалд хүргэж байрлуулах",
      "Rosemont орчмын баярын гэрэлтэй танилцах",
    ],
  },
  {
    day: 2,
    title: "Millennium Park Christmas",
    image: IMAGES.artInstitute,
    activities: [
      "Millennium Park-ийн Christmas tree",
      "Cloud Gate (The Bean) өвлийн зураг",
      "Holiday lights-тэй downtown Chicago",
      "Дулаан ундаа, хотын төвийн баярын алхалт",
    ],
  },
  {
    day: 3,
    title: "Christmas шоппинг өдөр",
    image: IMAGES.bean,
    activities: [
      "Fashion Outlets of Chicago",
      "Баярын хямдралтай худалдааны төвүүд",
      "Бэлэг худалдан авалт ба амралт",
    ],
  },
  {
    day: 4,
    title: "Өвлийн музей ба Skydeck",
    image: IMAGES.navyPier,
    activities: [
      "Shedd Aquarium",
      "Museum Campus",
      "Skydeck Chicago (Willis Tower)",
    ],
  },
  {
    day: 5,
    title: "Lincoln Park ZooLights",
    image: IMAGES.wrigley,
    activities: [
      "Lincoln Park ZooLights",
      "Өвлийн Lakefront аялал",
      "Баярын гэрэл, дурсгалын зураг",
    ],
  },
  {
    day: 6,
    title: "Magnificent Mile holiday lights",
    image: IMAGES.riverwalk,
    activities: [
      "Magnificent Mile-ийн баярын гэрэл",
      "Michigan Avenue Christmas чимэглэл",
      "Chinatown оройн хоол",
    ],
  },
  {
    day: 7,
    title: "Баярын дурсамж ба буцах",
    image: IMAGES.landmarks,
    activities: [
      "Бэлэг дурсгал, сүүлийн худалдан авалт",
      "Нисэх буудал хүргэх",
      "Буцах нислэг",
    ],
  },
];

export default function ChicagoTourPage() {
  useEffect(() => {
    document.title = "12-р сарын Чикаго Christmas аялал | United Dream Mongolia";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "$3,990 үнэтэй 12-р сарын Чикаго Christmas 7 хоногийн аялал. Онгоцны тийз, буудал, тээвэр, баярын үзвэр, даатгал, 3 удаагийн хоол, визний хураамж болон хөтөч багтсан."
      );
    }
  }, []);

  const scrollToItinerary = () => {
    document.getElementById("itinerary")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Christmas Chicago skyline background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        {/* Dark text-safe overlay on the left, with evergreen and holiday-red tones */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#081f18]/96 via-[#123b2a]/82 to-[#5b1221]/38" />
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: "radial-gradient(circle at 18% 18%, rgba(255,255,255,.85) 0 1px, transparent 2px), radial-gradient(circle at 66% 28%, rgba(255,255,255,.7) 0 1px, transparent 2px), radial-gradient(circle at 43% 73%, rgba(255,255,255,.6) 0 1px, transparent 2px)", backgroundSize: "76px 76px, 104px 104px, 132px 132px" }} />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />

        <div className="relative container mx-auto px-4 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

            {/* Left: text content */}
            <div>
              <Badge className="mb-4 border-red-200/30 bg-red-500/20 px-3 py-1 text-sm text-red-100">
                🎄 12-р сарын баярын тусгай аялал
              </Badge>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                Чикаго хотын
                <br />
                <span className="text-amber-300">Christmas аялал</span>
              </h1>

              <p className="text-white/80 text-lg mb-6">
                Цасан чимэгтэй Chicago, Millennium Park-ийн Christmas tree, holiday lights болон өвлийн хотын уур амьсгалыг 7 хоногт мэдрээрэй.
              </p>

              {/* Price box */}
              <div className="inline-flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 mb-8">
                <div>
                  <p className="text-white/60 text-sm mb-1">Нэг хүний үнэ</p>
                  <p className="text-5xl font-extrabold text-white">$3,990</p>
                  <p className="mt-1 text-sm text-amber-300">12-р сарын багц · Бүх зардал багтсан</p>
                </div>
                <div className="hidden sm:block w-px h-16 bg-white/20" />
                <div className="text-sm text-white/70 space-y-1">
                  <p className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />Онгоц, буудал, тээвэр багтсан</p>
                  <p className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />3 удаагийн хоол, даатгал багтсан</p>
                  <p className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />Визний хураамж, хөтөч багтсан</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={scrollToItinerary}
                  className="rounded-xl bg-amber-500 px-6 py-3 font-semibold text-black hover:bg-amber-400"
                >
                  Christmas хөтөлбөр үзэх
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button
                  onClick={scrollToContact}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-6 py-3 rounded-xl"
                >
                  Бүртгүүлэх
                </Button>
              </div>
            </div>

            {/* Right: Floating Honda Odyssey card */}
            <div className="flex items-center justify-center mt-6 lg:mt-0">
              <motion.div
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="relative"
              >
                {/* Amber glow behind card */}
                <div
                  className="absolute inset-0 rounded-3xl"
                  style={{
                    background: "radial-gradient(ellipse at 50% 80%, rgba(220,38,38,0.40) 0%, rgba(22,101,52,0.35) 55%, transparent 80%)",
                    filter: "blur(28px)",
                    transform: "scale(1.2) translateY(10%)",
                  }}
                />
                {/* Card */}
                <div
                  className="relative rounded-3xl overflow-hidden w-full max-w-[420px]"
                  style={{
                    boxShadow: "0 32px 80px rgba(0,0,0,0.65), 0 0 0 1px rgba(253,230,138,0.36), inset 0 1px 0 rgba(255,255,255,0.1)",
                  }}
                >
                  <img
                    src={IMAGES.hondaOdyssey}
                    alt="Chicago Christmas tour transport"
                    className="w-full h-48 sm:h-64 object-cover block"
                  />
                  {/* Bottom label */}
                  <div
                    className="absolute bottom-0 left-0 right-0 px-5 py-4"
                    style={{ background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, transparent 100%)" }}
                  >
                    <p className="text-amber-200 font-bold text-lg leading-tight">Баярын хотын тээвэр</p>
                    <p className="text-white/70 text-sm">8 суудалтай • Цастай хотын аялал</p>
                  </div>
                </div>
                {/* Floating badge top-right */}
                <motion.div
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-3 -right-3 rounded-full bg-red-600 px-3 py-1.5 text-xs font-bold text-white shadow-lg shadow-red-700/40"
                >
                  🎄 December tour
                </motion.div>
                {/* Floating badge bottom-left */}
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  className="absolute -bottom-3 -left-3 rounded-full bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white shadow-lg"
                >
                  📍 Чикаго, Иллинойс
                </motion.div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Agent discount notice ─────────────────────────────── */}
      <section className="border-b border-red-200 bg-red-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 shrink-0 mt-0.5 text-red-700" />
            <p className="text-sm text-red-900">
              <strong>Агентуудад 5%-ийн хөнгөлөлт!</strong> Манай агентын сүлжээнд нэгдсэн бол
              аяллын үнэнд <strong>5% буюу ~$200</strong> хөнгөлөлт эдэлнэ.
              <a href="/affiliate" className="ml-1 font-semibold text-red-800 underline">Агентаар бүртгүүлэх →</a>
            </p>
          </div>
        </div>
      </section>

      {/* ── Visa discount notice ─────────────────────────────── */}
      <section className="border-b border-emerald-200 bg-emerald-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 shrink-0 mt-0.5 text-emerald-700" />
            <p className="text-sm text-emerald-900">
              <strong>Хэрэв аялагч хүчинтэй АНУ-ын визтэй бол</strong> Элчин сайдын яамны визний
              хураамж болох <strong>$185</strong>-ыг багц үнээс хасна.
            </p>
          </div>
        </div>
      </section>

      {/* ── Included services ───────────────────────────────── */}
      <section className="bg-[#fffaf5] py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="mb-3 text-3xl font-bold text-gray-900">Christmas багцад юу багтсан вэ?</h2>
            <p className="text-gray-500">Доорх бүх үйлчилгээ $3,990 үнэд бүрэн багтсан болно</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {INCLUDED.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-xl border border-red-100 bg-white p-4"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#8b1e2d]">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-gray-800 text-sm font-medium leading-snug">{item.label}</p>
                </div>
              );
            })}
          </div>

          {/* Not included notice */}
          <div className="mt-8 max-w-2xl mx-auto p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <p className="text-gray-600 text-sm text-center">
              <strong className="text-gray-800">Анхаарна уу:</strong> Аялагчийн өөрийн гарын мөнгө,
              хувийн худалдан авалт болон хувийн нэмэлт зардал багц үнэд ороогүй болно.
            </p>
          </div>
        </div>
      </section>

      {/* ── Itinerary ───────────────────────────────────────── */}
      <section id="itinerary" className="bg-[#f4f8f6] py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="mb-3 text-3xl font-bold text-gray-900">Christmas аяллын хөтөлбөр</h2>
            <p className="text-gray-500">12-р сарын 7 өдрийн баярын уур амьсгалтай аялал</p>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-500">Баярын гэрэл, tree болон outdoor үзвэрийн нээлттэй өдөр нь тухайн аяллын бодит огноо, цаг агаараас хамааран баталгаажна.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {ITINERARY.map((day) => (
              <Card
                key={day.day}
                className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={day.image}
                    alt={day.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-4">
                    <span className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
                      {day.day}-р өдөр
                    </span>
                    <h3 className="text-white font-bold text-lg leading-tight">{day.title}</h3>
                  </div>
                </div>

                <CardContent className="p-4">
                  <ul className="space-y-2">
                    {day.activities.map((activity, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        {activity}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA / Contact ───────────────────────────────────── */}
      <section id="contact" className="bg-[#103a2a] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-4 text-3xl font-bold text-white">Christmas аялалд бүртгүүлэх</h2>
          <p className="mx-auto mb-8 max-w-lg text-emerald-100">
            Нэг хүний үнэ <strong className="text-amber-400 text-xl">$3,990</strong>. Одоо
            бүртгүүлж, 12-р сарын Чикаго Christmas аялалд хамрагдаарай.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+97680806633"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              <Phone className="w-4 h-4" />
              8080-6633 залгах
            </a>
            <a
              href="https://m.me/dreamvisamgl"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Messenger-ээр холбогдох
            </a>
          </div>

          <p className="mt-6 text-sm text-emerald-200">
            📞 Утас: <strong className="text-white text-lg">8080-6633</strong>
          </p>

          {/* Agent register CTA */}
          <div className="mt-10 border-t border-emerald-800 pt-8">
            <p className="mb-3 text-sm text-emerald-200">
              🌟 Агент болж <strong className="text-amber-400">5% хөнгөлөлт</strong> авах болон найздаа санал болгоод нэмэлт орлого олохыг хүсвэл:
            </p>
            <a
              href="/affiliate"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-black font-bold px-8 py-3 rounded-xl transition-all shadow-lg shadow-amber-500/30"
            >
              <Users className="w-5 h-5" />
              Агентаар бүртгүүлэх
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
